import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DEV_ACCOUNTS, isLocalDev } from '../config/authRoles';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldCheck, UserCheck, Shield, Building2, User } from 'lucide-react';

const ROLE_ICONS = {
  TA_USER: UserCheck,
  SUPER_ADMIN: ShieldCheck,
  TA_ADMIN: Shield,
  MERCCHANTADMIN: Building2,
  EMPLOYEE: User,
};

const Login = () => {
  const navigate = useNavigate();
  const { login, devLogin, isDev } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeDevRole, setActiveDevRole] = useState(null);

  const attemptLogin = async (loginEmail, loginPassword) => {
    setSubmitting(true);
    const result = await login(loginEmail, loginPassword);
    setSubmitting(false);
    if (!result?.success) {
      setError(result?.error || 'Login failed. Please try again.');
      return;
    }
    navigate('/dashboard');
  };

  const handleQuickDevLogin = async (roleKey) => {
    setError('');
    setActiveDevRole(roleKey);
    setSubmitting(true);

    // Subtle micro-delay for responsive visual feedback
    await new Promise((resolve) => setTimeout(resolve, 150));

    const result = devLogin(roleKey);
    setSubmitting(false);
    setActiveDevRole(null);

    if (!result?.success) {
      setError(result?.error || 'Quick login failed');
      return;
    }
    navigate('/dashboard');
  };

  const handleFillCredentials = (account) => {
    setEmail(account.email);
    setPassword(account.password);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    await attemptLogin(email, password);
  };

  const showDevPanel = isDev ?? isLocalDev();

  return (
    <div className="min-h-screen bg-[#0D1F2D] flex items-center justify-center relative overflow-hidden py-10">
      {/* Background Decorative Elements */}
      <div className="absolute left-0 top-0 w-96 h-96 rounded-full bg-blue-600 opacity-20 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div
        className="absolute right-0 top-0 w-64 h-64 bg-orange-500 opacity-30"
        style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }}
      ></div>

      {/* Header */}
      <div className="absolute top-6 left-6">
        <div className="flex items-center space-x-2">
          <svg className="w-8 h-8 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
          </svg>
          <span className="text-white text-xl font-bold">ApurvaX</span>
          <span className="text-gray-400 text-xl">Payroll</span>
        </div>
      </div>

      {/* Top Right Link */}
      <div className="absolute top-6 right-6">
        <a href="https://Apurva.com" className="text-sm text-gray-400 hover:text-white transition-colors">
          New to ApurvaX Payroll? Click here for our homepage!
        </a>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-lg px-6 relative z-10">
        <div className="bg-[#1E2A34] rounded-xl p-8 shadow-2xl border border-gray-800/80">
          <h2 className="text-2xl font-semibold text-white mb-2 text-center">
            Smart payroll for smarter businesses
          </h2>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div>
              <Label htmlFor="email" className="text-gray-300 text-sm">
                Email Address or Username
              </Label>
              <Input
                id="email"
                type="text"
                placeholder="Enter your email or username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <Label htmlFor="password" className="text-gray-300 text-sm">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
              />
            </div>

            {error && <div className="text-red-400 text-sm">{error}</div>}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 text-base font-medium transition-colors"
            >
              {submitting && !activeDevRole ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          {/* Local Dev Quick Access */}
          {showDevPanel && (
            <div className="mt-6 pt-5 border-t border-gray-700/60">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
                    Local Dev Quick Login
                  </span>
                </div>
                <span className="text-[11px] text-gray-400">
                  Password: <code className="text-blue-300 font-mono">password123</code>
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {Object.values(DEV_ACCOUNTS).map((account) => {
                  const Icon = ROLE_ICONS[account.roleKey] || UserCheck;
                  const isCurrentActive = activeDevRole === account.roleKey;
                  return (
                    <div
                      key={account.roleKey}
                      className="group relative flex flex-col justify-between p-3 rounded-lg bg-[#0F1B26] border border-gray-700/60 hover:border-blue-500/50 hover:bg-[#142332] transition-all"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-1.5">
                            <Icon className="w-4 h-4 text-gray-400 group-hover:text-blue-400 transition-colors" />
                            <span className="text-sm font-semibold text-white group-hover:text-blue-300 transition-colors">
                              {account.label}
                            </span>
                          </div>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${account.badgeColor}`}
                          >
                            {account.badge}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400 truncate font-mono mb-2" title={account.email}>
                          {account.email}
                        </p>
                      </div>

                      <div className="flex items-center space-x-2 pt-2 border-t border-gray-800/80">
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => handleQuickDevLogin(account.roleKey)}
                          className="flex-1 bg-blue-600/80 hover:bg-blue-600 active:bg-blue-700 text-white text-xs font-medium py-1.5 px-2 rounded transition-colors text-center disabled:opacity-50"
                        >
                          {isCurrentActive ? 'Logging in...' : 'Login'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFillCredentials(account)}
                          className="text-xs text-gray-400 hover:text-white px-2 py-1.5 rounded hover:bg-gray-800/60 border border-gray-700/50 transition-colors"
                          title="Fill credentials into login form"
                        >
                          Fill
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="mt-2.5 text-[11px] text-gray-400 text-center">
                Click <strong>Login</strong> for 1-click access, or <strong>Fill</strong> to populate the form above.
              </p>
            </div>
          )}

          <div className="mt-5 text-center">
            <span className="text-gray-400 text-sm">Not a customer yet? </span>
            <Link
              to="/signup"
              className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
            >
              Sign Up!
            </Link>
          </div>

          <div className="mt-6 text-center text-xs text-gray-500">
            This site is protected by reCAPTCHA and the Google
            <br />
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-300">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-300">
              Terms of Service
            </a>{' '}
            apply.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
